<template>
  <transition name="slide-down">
    <div v-if="showBanner" class="pwa-toast">
      <div class="toast-header">
        <strong>{{ title }}</strong>
        <button class="close-btn" @click="dismiss">&times;</button>
      </div>

      <p class="toast-message" v-html="message"></p>

      <!-- SINGLE BUTTON -->
      <button
        v-if="!actionDone && !isIOSUnsupported"
        @click="completeSetup"
        class="toast-btn btn btn-primary"
      >
        {{ buttonText }}
      </button>

      <!-- iOS fallback message -->
      <p v-if="isIOSUnsupported" class="toast-message">
        On iOS, add this app to your Home Screen manually: tap <strong>Share → Add to Home Screen</strong>.
      </p>
    </div>
  </transition>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      showBanner: false,
      title: "Install & Enable Notifications",
      message: "Get the full experience by installing the app and enabling push notifications.",
      deferredPrompt: null,
      actionDone: false,
      isSubscribed: false,
      isInstalled: false,
      isIOS: false,
      isIOSUnsupported: false,
      buttonText: "Install App & Enable Notifications",
    };
  },

  async mounted() {
    console.log("[PWA DEBUG] Mounted");
    this.detectPlatform();
    this.listenForInstallPrompt();
    await this.checkSubscription();
    this.checkInstalled();
    this.handleIOSUnsupported();
    this.decideToShowBanner();
  },

  methods: {
    // Detect platform
    detectPlatform() {
      this.isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
      console.log("[PWA DEBUG] Platform detected. isIOS:", this.isIOS);
    },

    // iOS Safari cannot use deferred install prompt
    handleIOSUnsupported() {
      if (this.isIOS && !("BeforeInstallPromptEvent" in window)) {
        this.isIOSUnsupported = true;
        console.log("[PWA DEBUG] iOS detected: manual install required");
      }
    },

    // Listen for PWA install prompt
    listenForInstallPrompt() {
      window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        this.deferredPrompt = e;
        console.log("[PWA DEBUG] deferredPrompt captured:", this.deferredPrompt);
        this.decideToShowBanner();
      });
    },

    // Check if already subscribed
    async checkSubscription() {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

      console.log("[PWA DEBUG] Checking push subscription...");
      const reg = await navigator.serviceWorker.ready;
      console.log("[PWA DEBUG] ServiceWorker ready", reg);

      const sub = await reg.pushManager.getSubscription();
      console.log("[PWA DEBUG] Current subscription:", sub);
      this.isSubscribed = !!sub;
    },

    // Check if app is installed (deferred prompt or iOS fallback)
    checkInstalled() {
      this.isInstalled = localStorage.getItem("pwa-installed") === "1";
      console.log("[PWA DEBUG] checkInstalled - isInstalled:", this.isInstalled);
    },

    // Decide if toast should show
    decideToShowBanner() {
      console.log("[PWA DEBUG] decideToShowBanner");

      if (localStorage.getItem("pwa-dismissed")) {
        console.log("[PWA DEBUG] Banner dismissed previously");
        return;
      }

      // Determine button text
      if (!this.isInstalled && !this.isSubscribed) {
        this.buttonText = "Install App & Enable Notifications";
      } else if (this.isInstalled && !this.isSubscribed) {
        this.buttonText = "Enable Notifications";
      } else if (!this.isInstalled && this.isSubscribed) {
        this.buttonText = "Install App";
      } else {
        console.log("[PWA DEBUG] Already installed & subscribed, no banner needed");
        return;
      }

      this.showBanner = true;
      console.log("[PWA DEBUG] Showing banner. isSubscribed:", this.isSubscribed, "isInstalled:", this.isInstalled, "isIOSUnsupported:", this.isIOSUnsupported);
    },

    // ONE BUTTON - combined action
    async completeSetup() {
      console.log("[PWA DEBUG] completeSetup clicked");

      try {
        // Step 1: Notification permission
        if (!this.isSubscribed) {
          console.log("[PWA DEBUG] Requesting Notification permission...");
          const permission = await Notification.requestPermission();
          console.log("[PWA DEBUG] Notification permission:", permission);
          if (permission !== "granted") return;

          // Step 2: Register service worker
          const registration = await navigator.serviceWorker.register("/serviceworker.js");
          await navigator.serviceWorker.ready;
          console.log("[PWA DEBUG] ServiceWorker registered: ", registration);

          // Step 3: Subscribe to push
          const vapidPublicKey = process.env.MIX_VAPID_PUBLIC_KEY;
          const key = this.urlBase64ToUint8Array(vapidPublicKey);

          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: key,
          });

          await axios.post("/api/save-subscription", subscription);
          console.log("[PWA DEBUG] Push subscription created and sent to server:", subscription);

          this.isSubscribed = true;
        }

        // Step 4: Prompt install if needed
        if (!this.isInstalled && this.deferredPrompt) {
          console.log("[PWA DEBUG] Prompting install...");
          this.deferredPrompt.prompt();
          const choice = await this.deferredPrompt.userChoice;
          console.log("[PWA DEBUG] userChoice:", choice);
          this.deferredPrompt = null;

          if (choice.outcome === "accepted") {
            this.isInstalled = true;
            localStorage.setItem("pwa-installed", "1");
          }
        }

        // Step 5: Close banner forever
        this.actionDone = true;
        this.showBanner = false;
        localStorage.setItem("pwa-dismissed", "1");
        console.log("[PWA DEBUG] Banner closed and dismissed");

      } catch (e) {
        console.error("[PWA Setup Error]", e);
      }
    },

    // Hide toast manually
 async dismiss() {
  try {
    this.showBanner = false;
    localStorage.setItem("pwa-dismissed", "1");

    // Send to server
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();

    await axios.post("/api/save-subscription", {
      subscription: sub,         // could be null if never subscribed
      dismissed: true,           // mark as dismissed
      url: window.location.href, // track URL
    });

    console.log("[PWA DEBUG] Banner dismissed saved to server");
  } catch (e) {
    console.error("[PWA DEBUG] Failed to save dismissal:", e);
  }
},

    // Helper for VAPID key
    urlBase64ToUint8Array(base64String) {
      const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
      const raw = window.atob(base64);
      return new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
    },
  },
};
</script>

<style scoped>
.pwa-toast {
  position: fixed;
  top: 15px;
  left: 15px;
  width: 280px;
  padding: 15px 18px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  z-index: 99999;
  border: 1px solid rgba(0,0,0,0.08);
}

.toast-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  opacity: 0.6;
  cursor: pointer;
}
.close-btn:hover {
  opacity: 1;
}

.toast-message {
  margin: 10px 0;
}

/* Slide Animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all .35s ease;
}
.slide-down-enter {
  transform: translateY(-120%);
  opacity: 0;
}
.slide-down-leave-to {
  transform: translateY(-120%);
  opacity: 0;
}
</style>
